import { emitter } from '@phoenix/event-bus';

export function createLocalStorageHelper({
  name,
  updateEventName,
  initialValue,
  parse = JSON.parse,
  serialize = JSON.stringify,
  scopeToCountry = true,
}) {
  const defaultCountryCode = window.Phoenix.defaultCountryCode;
  const storageKey = scopeToCountry ? `${name}_${defaultCountryCode}` : name;

  function readRaw() {
    const ls = localStorage.getItem(storageKey);
    return ls ?? null;
  }

  function get() {
    const raw = readRaw();
    if (raw == null) return initialValue;
    try {
      return parse(raw);
    } catch {
      return initialValue;
    }
  }

  function save(value) {
    const serialized = serialize(value);
    localStorage.setItem(storageKey, serialized);
    return value;
  }

  // only valid when working over an array
  function add(item) {
    const list = get();
    if (Array.isArray(list) && !list.includes(item)) {
      return save([...list, item]);
    }
    return list;
  }

  function remove(item) {
    const list = get();
    if (Array.isArray(list)) {
      return save(list.filter((i) => i !== item));
    }
    return list;
  }

  function clear() {
    localStorage.removeItem(storageKey);
  }

  function init() {
    // Ensure that something is in storage (either the existing value or the initialValue)
    save(get());
  }

  // Listen for changes in other tabs/windows
  window.addEventListener('storage', (e) => {
    if (e.key === storageKey) {
      const newValue = e.newValue ? parse(e.newValue) : initialValue;
      emitter.emit(updateEventName, newValue);
    } else if (e.key === null && e.newValue === null && e.oldValue === null) {
      // This handles the case where localStorage is cleared
      emitter.emit(updateEventName, initialValue);
    }
  });

  init();

  return { get, save, add, remove, clear };
}
